// src/components/GridView/renderers/GridCellEditor.tsx
import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FIELD_TYPES } from '../types';
import { MultiSelectField } from '@components/SubmitTime/fields/MultiSelectField';
import { ComboBoxField } from '@components/SubmitTime/fields/ComboBoxField';
import { InputDateField } from '@components/SubmitTime/fields/InputDateField';
import type { EditingCellState, GridCellRect } from '../types';

// ==================== Types ====================

export interface GridCellEditorProps {
  editingCell: EditingCellState;
  /** onSave(saveValue, displayValue?) — displayValue is shown in the cell; saveValue goes to the API */
  onSave: (saveValue: any, displayValue?: string) => void;
  onCancel: () => void;
  configValueId?: number;
  projectId?: string;
  /** Re-measures the edited cell. Returns null while the cell is not rendered. */
  getCellRect?: () => GridCellRect | null;
  /** Called after a keyboard commit so the grid can move the selection down one row. */
  onCommitWithEnter?: () => void;
  /**
   * Called with the click coordinates when the full-viewport backdrop is clicked to save and
   * close. The backdrop (position:fixed, inset:0) sits above the whole grid so a click "away"
   * always reaches it first, not whatever grid cell is visually underneath — including a click
   * that was actually meant to start editing the NEXT cell. Without this, that click only
   * closes the current editor; HOT's selection stays on the cell just saved, so the very next
   * keystroke (typed expecting to land in the new cell) reopens THAT same cell instead and
   * overwrites the value just saved to it. The grid uses this to move HOT's selection to
   * whatever is actually under the click, once the backdrop is gone, so typing continues into
   * the right cell.
   */
  onOutsideClick?: (clientX: number, clientY: number) => void;
}

/** Sub-editor props, which receive an already-live rect. */
type SubEditorProps = Omit<GridCellEditorProps, 'getCellRect'> & { cellRect: GridCellRect };

// ==================== Helpers ====================

/**
 * Keeps the floating editor aligned with its cell on scroll and resize. Re-measures through a
 * callback rather than caching the <td>, since Handsontable recycles them while virtualising.
 */
function useLiveCellRect(
  editingCell: EditingCellState,
  getCellRect?: () => GridCellRect | null,
): GridCellRect
{
  const [rect, setRect] = useState<GridCellRect>(editingCell.cellRect);

  // Editing can move to another cell without remounting, leaving the initial rect stale.
  useEffect(() =>
  {
    setRect(editingCell.cellRect);
  }, [editingCell.cellRect]);

  useEffect(() =>
  {
    if (!getCellRect) return;

    let frame = 0;
    const update = () =>
    {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
      {
        const next = getCellRect();
        if (!next) return;
        setRect(prev =>
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
            ? prev
            : next,
        );
      });
    };

    // Capture phase: Handsontable scrolls an inner .wtHolder, which does not bubble.
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    return () =>
    {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, [getCellRect]);

  return rect;
}

function getInitialValue(editingCell: EditingCellState): string
{
  return String(editingCell.currentValue ?? '');
}

/**
 * Keeps editor keystrokes from reaching Handsontable. Without this, the Enter that saves
 * carries on to the grid, which reopens the editor showing the pre-save value.
 */
const stopKeysReachingGrid = (e: React.KeyboardEvent) => e.stopPropagation();

function isValidNumeric(value: string, allowDecimal: boolean): boolean
{
  if (value === '' || value === '-') return true;
  const pattern = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d+$/;
  return pattern.test(value);
}

function ensureProtocol(url: string): string
{
  if (!url || url.trim() === '') return '';
  const trimmed = url.trim();
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('ftp://')
  )
  {
    return trimmed;
  }
  return 'http://' + trimmed;
}

// ==================== Combo editor (single select, reuses ComboBoxField from SubmitTime) ====================

function ComboBoxEditor({ editingCell, onSave, cellRect, onOutsideClick }: SubEditorProps)
{
  const stagedValueRef = useRef<string>(String(editingCell.customField?.Value ?? ''));
  const stagedDisplayRef = useRef<string>(String(editingCell.customField?.FieldValue ?? ''));
  const hasSavedRef = useRef(false);

  const handleSave = () =>
  {
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;
    onSave(stagedValueRef.current, stagedDisplayRef.current || undefined);
  };

  // Single-select: save immediately on selection, no need to blur
  const handleDynamicInputChange = (_fieldId: number, value: any, displayText?: string) =>
  {
    stagedValueRef.current = String(value ?? '');
    if (displayText !== undefined) stagedDisplayRef.current = displayText;
    handleSave();
  };

  const customFieldForSelect = {
    ...editingCell.customField,
    Value: editingCell.customField?.Value,
    SelectedValuesText: editingCell.customField?.FieldValue,
    ConversationIsEnable: true,
  };

  const MIN_COMBO_WIDTH = 280;
  const comboWidth = Math.max(cellRect.width, MIN_COMBO_WIDTH);
  // If the dropdown would overflow the right edge of the viewport, anchor it to the right of the cell instead
  const comboLeft =
    cellRect.left + comboWidth > window.innerWidth
      ? Math.max(0, cellRect.left + cellRect.width - comboWidth)
      : cellRect.left;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: cellRect.top,
    left: comboLeft,
    width: comboWidth,
    zIndex: 9999,
  };

  return createPortal(
    <>
      <div
        className="gridview-cell-editor-backdrop"
        onClick={e =>
        {
          handleSave();
          onOutsideClick?.(e.clientX, e.clientY);
        }}
      />
      <div style={containerStyle} onKeyDown={stopKeysReachingGrid}>
        <ComboBoxField
          customField={customFieldForSelect}
          canEdit={true}
          handleDynamicInputChange={handleDynamicInputChange}
          UpdateCustomFields={handleSave}
          autoFocus={true}
        />
      </div>
    </>,
    document.body,
  );
}

// ==================== MultiSelect editor ====================

function MultiSelectEditor({
  editingCell,
  onSave,
  configValueId,
  projectId,
  cellRect,
  onOutsideClick,
}: SubEditorProps)
{
  const stagedValueRef = useRef<string>(String(editingCell.customField?.Value ?? ''));
  const stagedDisplayRef = useRef<string>(String(editingCell.customField?.FieldValue ?? ''));
  const hasSavedRef = useRef(false);
  const { column } = editingCell;

  const cardConfigValueId = (editingCell.conversation as any).ConfigValueId ?? configValueId;

  const handleSave = () =>
  {
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;
    onSave(stagedValueRef.current, stagedDisplayRef.current || undefined);
  };

  // Stage values on every selection change — do NOT close here (user may pick more)
  const handleDynamicInputChange = (_fieldId: number, value: any, displayText?: string) =>
  {
    stagedValueRef.current = String(value ?? '');
    if (displayText !== undefined) stagedDisplayRef.current = displayText;
  };

  // Called on react-select blur — save with whatever was staged and close
  const handleClose = () => handleSave();

  const customFieldForSelect = {
    ...editingCell.customField,
    ConversationIsEnable: true,
  };

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: cellRect.top,
    left: cellRect.left,
    width: Math.max(cellRect.width, 220),
    zIndex: 9999,
  };

  return createPortal(
    <>
      <div
        className="gridview-cell-editor-backdrop"
        onClick={e =>
        {
          handleSave();
          onOutsideClick?.(e.clientX, e.clientY);
        }}
      />
      <div style={containerStyle} onKeyDown={stopKeysReachingGrid}>
        <MultiSelectField
          customField={customFieldForSelect}
          canEdit={true}
          configValueId={cardConfigValueId}
          layerId={column.layerId}
          projectId={projectId ? parseInt(projectId, 10) : undefined}
          handleDynamicInputChange={handleDynamicInputChange}
          UpdateCustomFields={handleClose}
          autoFocus={true}
        />
      </div>
    </>,
    document.body,
  );
}

// ==================== Date editor ====================

function DateEditor({ editingCell, onSave, cellRect, onOutsideClick }: SubEditorProps)
{
  const stagedValueRef = useRef<string>(String(editingCell.customField?.Value ?? ''));
  const stagedDisplayRef = useRef<string>(String(editingCell.customField?.FieldValue ?? ''));
  const hasSavedRef = useRef(false);

  const handleSave = () =>
  {
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;
    onSave(stagedValueRef.current, stagedDisplayRef.current || undefined);
  };

  // Stage value on date selection — both save value and display use mm/dd/yyyy
  const handleDynamicInputChange = (_fieldId: number, value: any) =>
  {
    if (value)
    {
      try
      {
        const d = new Date(value);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const formatted = `${mm}/${dd}/${d.getFullYear()}`;
        stagedValueRef.current = formatted;
        stagedDisplayRef.current = formatted;
      }
      catch
      {
        stagedValueRef.current = String(value);
        stagedDisplayRef.current = String(value);
      }
    }
    else
    {
      stagedValueRef.current = '';
      stagedDisplayRef.current = '';
    }
  };

  const customFieldForDate = {
    ...editingCell.customField,
    Value: editingCell.customField?.Value ?? null,
    ConversationIsEnable: true,
  };

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: cellRect.top,
    left: cellRect.left,
    width: Math.max(cellRect.width, 160),
    zIndex: 9999,
  };

  return createPortal(
    <>
      <div
        className="gridview-cell-editor-backdrop"
        onClick={e =>
        {
          handleSave();
          onOutsideClick?.(e.clientX, e.clientY);
        }}
      />
      <div style={containerStyle} onKeyDown={stopKeysReachingGrid}>
        <InputDateField
          customField={customFieldForDate}
          canEdit={true}
          handleDynamicInputChange={handleDynamicInputChange}
          UpdateCustomFields={handleSave}
          autoOpen={true}
        />
      </div>
    </>,
    document.body,
  );
}

// ==================== Main dispatcher ====================

export function GridCellEditor({
  editingCell,
  onSave,
  onCancel,
  configValueId,
  projectId,
  getCellRect,
  onCommitWithEnter,
  onOutsideClick,
}: GridCellEditorProps)
{
  const [value, setValue] = useState<string>(() => getInitialValue(editingCell));
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  // Guards the inline/multiline paths below against firing onSave (or onCancel) more than
  // once for the same edit session. Unlike ComboBoxEditor/MultiSelectEditor/DateEditor above
  // (which each have their own hasSavedRef), this path had no such guard, so a rapid double
  // click landed on both the backdrop (its onClick has no stopPropagation) and, in the same
  // burst, an Enter/blur commit — both fired onSave with whatever was staged, reaching the
  // grid's async handleCellSave twice in quick succession. That's the "API called multiple
  // times in a short period" behavior from the bug report: values getting dropped, or a
  // fast-typed value from one field landing on the next cell clicked while the previous
  // save was still in flight.
  const hasResolvedRef = useRef(false);
  const { fieldTypeId } = editingCell.column;
  // Before the early returns below, so hook order stays stable across field types.
  const cellRect = useLiveCellRect(editingCell, getCellRect);

  const isMultiline = fieldTypeId === FIELD_TYPES.MULTILINE_TEXT;
  const isInteger = fieldTypeId === FIELD_TYPES.INTEGER;
  const isDecimal = fieldTypeId === FIELD_TYPES.DECIMAL || fieldTypeId === FIELD_TYPES.INTEGER_ALT;
  const isNumeric = isInteger || isDecimal;
  const isHyperlink = fieldTypeId === FIELD_TYPES.HYPERLINK;
  const isCombo = fieldTypeId === FIELD_TYPES.COMBO || fieldTypeId === FIELD_TYPES.COMBO_ALT;
  const isMulti = fieldTypeId === FIELD_TYPES.MULTI_SELECT;
  const isDate = fieldTypeId === FIELD_TYPES.DATE;
  const isInline = !isMultiline && !isDate && !isCombo && !isMulti;

  // ── Auto-focus for text/date/multiline editors ──
  useEffect(() =>
  {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if ('select' in el) el.select();
  }, []);

  // ── Dispatch to specialised editors ──
  if (isCombo)
    return (
      <ComboBoxEditor
        editingCell={editingCell}
        onSave={onSave}
        onCancel={onCancel}
        cellRect={cellRect}
        onOutsideClick={onOutsideClick}
      />
    );
  if (isMulti)
    return (
      <MultiSelectEditor
        editingCell={editingCell}
        onSave={onSave}
        onCancel={onCancel}
        configValueId={configValueId}
        projectId={projectId}
        cellRect={cellRect}
        onOutsideClick={onOutsideClick}
      />
    );
  if (isDate)
    return (
      <DateEditor
        editingCell={editingCell}
        onSave={onSave}
        onCancel={onCancel}
        cellRect={cellRect}
        onOutsideClick={onOutsideClick}
      />
    );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
  {
    const next = e.target.value;
    if (isNumeric)
    {
      if (!isValidNumeric(next, isDecimal)) return;
    }
    setValue(next);
  };

  // Every exit from this edit session (save or cancel) funnels through here, so whichever
  // fires first — a rapid second click on the backdrop, Enter racing a blur, Escape racing a
  // click — wins, and every later one is a no-op instead of reaching the grid's onSave again.
  const resolveOnce = (action: () => void) =>
  {
    if (hasResolvedRef.current) return;
    hasResolvedRef.current = true;
    action();
  };

  /** Returns false when the value was rejected and the edit was cancelled. */
  const handleSave = (): boolean =>
  {
    if (hasResolvedRef.current) return false;

    if (isNumeric && value !== '')
    {
      const parsed = Number(value);
      if (isNaN(parsed))
      {
        resolveOnce(onCancel);
        return false;
      }
      resolveOnce(() => onSave(isInteger ? String(Math.trunc(parsed)) : String(parsed)));
      return true;
    }
    resolveOnce(() => onSave(value));
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent) =>
  {
    if (e.key === 'Escape')
    {
      e.preventDefault();
      resolveOnce(onCancel);
      return;
    }
    if (isMultiline)
    {
      if (e.key === 'Enter' && e.ctrlKey)
      {
        e.preventDefault();
        if (handleSave()) onCommitWithEnter?.();
      }
    }
    else
    {
      if (e.key === 'Enter')
      {
        e.preventDefault();
        if (isHyperlink)
        {
          if (!hasResolvedRef.current)
          {
            resolveOnce(() => onSave(ensureProtocol(value)));
            onCommitWithEnter?.();
          }
        }
        else if (handleSave())
        {
          onCommitWithEnter?.();
        }
      }
    }
  };

  // ── Inline editor (TEXT, HYPERLINK, INTEGER, DECIMAL) ──
  if (isInline)
  {
    const wrapperStyle: React.CSSProperties = {
      top: cellRect.top,
      left: cellRect.left,
      width: cellRect.width,
      height: cellRect.height,
    };
    return createPortal(
      <>
        <div
          className="gridview-cell-editor-backdrop"
          onClick={e =>
          {
            handleSave();
            onOutsideClick?.(e.clientX, e.clientY);
          }}
        />
        <div
          className="gridview-cell-editor-inline"
          style={wrapperStyle}
          onKeyDown={stopKeysReachingGrid}
        >
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            className="gridview-cell-editor-inline-input"
            type={fieldTypeId === FIELD_TYPES.HYPERLINK ? 'url' : 'text'}
            inputMode={isDecimal ? 'decimal' : isInteger ? 'numeric' : undefined}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        </div>
      </>,
      document.body,
    );
  }

  // ── Floating editor (MULTILINE_TEXT) ──
  const wrapperStyle: React.CSSProperties = {
    top: cellRect.top,
    left: cellRect.left,
    minWidth: cellRect.width,
  };

  return createPortal(
    <>
      <div
        className="gridview-cell-editor-backdrop"
        onClick={e =>
        {
          handleSave();
          onOutsideClick?.(e.clientX, e.clientY);
        }}
      />
      <div
        className="gridview-cell-editor-wrapper"
        style={wrapperStyle}
        onKeyDown={stopKeysReachingGrid}
      >
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          className="gridview-cell-editor-input gridview-cell-editor-textarea"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={4}
        />
        <div className="gridview-cell-editor-actions">
          <button
            type="button"
            className="gridview-cell-editor-btn gridview-cell-editor-btn-save"
            onClick={handleSave}
          >
            Save
          </button>
          <button
            type="button"
            className="gridview-cell-editor-btn gridview-cell-editor-btn-cancel"
            onClick={() => resolveOnce(onCancel)}
          >
            Cancel
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
